import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import LocationAutocomplete from '../components/LocationAutocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { API_URL, jobTypeOptions, licenseOptions } from '../lib/utils';
import { TECH_ADMIN_OPTIONS, TECH_ADMIN_DISCLAIMER } from '../lib/techAdminSupport';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Plus, X, Star, Sparkles, CheckCircle2, Wand2, AlertTriangle, CreditCard } from 'lucide-react';

const PostJob = () => {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isEmployer, isAdmin } = useAuth();
  const isEditing = !!jobId;
  const isWelcome = searchParams.get('welcome') === 'true';
  
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEditing);
  const [wantsFeatured, setWantsFeatured] = useState(false);
  const [generatingDescription, setGeneratingDescription] = useState(false);
  const [showFeatureConfirmDialog, setShowFeatureConfirmDialog] = useState(false);
  const [pendingJobData, setPendingJobData] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    company_name: user?.company_name || user?.name || '',
    location: '',
    job_type: 'full-time',
    license_required: 'LMSW',
    salary_min: '',
    salary_max: '',
    pay_type: 'salary',
    pay_frequency: 'year',
    description: '',
    requirements: [''],
    benefits: [''],
    is_remote: false,
    zip_code: '',
    remote_type: 'in_person',
    specialty: '',
    lgbtq_affirming: false,
    veteran_friendly: false,
    accessible_inclusive: false,
    tech_admin_support: [],
    tech_admin_support_other: '',
    application_method: 'inasw',
    duration_days: 30,
  });

  const [atsConnected, setAtsConnected] = useState(false);
  useEffect(() => {
    axios.get(`${API_URL}/ats/my-integration`).then((r) => {
      const integ = r.data?.integration;
      setAtsConnected(integ?.status === 'connected' && integ?.recurring_addon_status === 'active');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEmployer && !isAdmin) {
      toast.error('Only employers can post jobs');
      navigate('/');
      return;
    }

    if (isEditing) {
      fetchJob();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, isEmployer, isAdmin]);

  const fetchJob = async () => {
    try {
      const response = await axios.get(`${API_URL}/jobs/${jobId}`);
      const job = response.data;
      setFormData({
        title: job.title,
        company_name: job.company_name,
        location: job.location,
        job_type: job.job_type,
        license_required: job.license_required,
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        pay_type: job.pay_type || 'salary',
        pay_frequency: job.pay_frequency || 'year',
        description: job.description,
        requirements: job.requirements?.length > 0 ? job.requirements : [''],
        benefits: job.benefits?.length > 0 ? job.benefits : [''],
        is_remote: job.is_remote,
        zip_code: job.zip_code || '',
        remote_type: job.remote_type || (job.is_remote ? 'remote' : 'in_person'),
        specialty: job.specialty || '',
        lgbtq_affirming: !!job.lgbtq_affirming,
        veteran_friendly: !!job.veteran_friendly,
        accessible_inclusive: !!job.accessible_inclusive,
        tech_admin_support: job.tech_admin_support || [],
        tech_admin_support_other: job.tech_admin_support_other || '',
      });
    } catch (error) {
      toast.error('Failed to load job');
      navigate('/employer/dashboard');
    } finally {
      setFetching(false);
    }
  };

  // AI Job Description Generator
  const generateAIDescription = async () => {
    if (!formData.title) {
      toast.error('Please enter a job title first');
      return;
    }
    
    setGeneratingDescription(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/generate-job-description`,
        {
          title: formData.title,
          license_type: formData.license_required,
          job_type: formData.job_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()),
          location: formData.location || 'United States',
          is_remote: formData.is_remote,
          salary_range: formData.salary_min && formData.salary_max 
            ? `$${formData.salary_min} - $${formData.salary_max}` 
            : null,
          requirements: formData.requirements.filter(r => r.trim()).join(', '),
          company: formData.company_name
        },
        { headers: {  } }
      );
      
      if (response.data.success && response.data.description) {
        // Strip markdown code blocks and HTML tags for plain text description
        let plainText = response.data.description
          .replace(/```\w*\n?|```/g, '')  // Remove markdown code block markers
          .replace(/<[^>]*>/g, '')         // Remove HTML tags
          .trim();
        setFormData(prev => ({ ...prev, description: plainText }));
        toast.success('Job description generated! Feel free to edit it.');
      }
    } catch (error) {
      console.error('AI generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate description');
    } finally {
      setGeneratingDescription(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.description || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Compensation is required for direct INASW job posts
    if (!formData.salary_min || !formData.salary_max || !formData.pay_type || !formData.pay_frequency) {
      toast.error('Compensation is required — please enter a pay type, frequency, and a minimum & maximum amount.');
      return;
    }
    if (parseInt(formData.salary_min) > parseInt(formData.salary_max)) {
      toast.error('Minimum pay cannot be greater than maximum pay.');
      return;
    }

    const payload = {
      ...formData,
      salary_min: formData.salary_min ? parseInt(formData.salary_min) : null,
      salary_max: formData.salary_max ? parseInt(formData.salary_max) : null,
      requirements: formData.requirements.filter(r => r.trim()),
      benefits: formData.benefits.filter(b => b.trim()),
    };

    // If user wants featured job and it's a new job, show confirmation dialog first
    if (wantsFeatured && !isEditing) {
      setPendingJobData(payload);
      setShowFeatureConfirmDialog(true);
      return;
    }

    // Otherwise proceed with normal posting
    await submitJob(payload, false);
  };

  // Handle featured job payment flow
  const handleFeatureConfirm = async () => {
    setShowFeatureConfirmDialog(false);
    setLoading(true);
    
    try {
      
      // First, post the job
      const response = await axios.post(`${API_URL}/jobs`, pendingJobData, {
        headers: {  },
        withCredentials: true,
      });
      
      if (!response.data.job_id) {
        throw new Error('Job creation failed');
      }
      
      toast.success('Job posted! Redirecting to payment...');
      
      // Then redirect to Stripe checkout for featuring
      const originUrl = window.location.origin;
      const featureResponse = await axios.post(
        `${API_URL}/jobs/${response.data.job_id}/feature`,
        { origin_url: originUrl },
        { headers: {  }, withCredentials: true }
      );
      
      // Redirect to Stripe
      window.location.href = featureResponse.data.url;
    } catch (error) {
      console.error('Feature checkout error:', error);
      toast.error('Failed to start payment. Your job was saved as a free listing.');
      navigate('/employer/dashboard');
    } finally {
      setLoading(false);
      setPendingJobData(null);
    }
  };

  // Handle skipping the feature payment - post as free
  const handleFeatureSkip = async () => {
    setShowFeatureConfirmDialog(false);
    setWantsFeatured(false);
    await submitJob(pendingJobData, true);
    setPendingJobData(null);
  };

  // Core job submission logic
  const submitJob = async (payload, showSkipWarning = false) => {
    setLoading(true);
    try {

      let response;
      if (isEditing) {
        response = await axios.put(`${API_URL}/jobs/${jobId}`, payload, {
          headers: {  },
          withCredentials: true,
        });
        toast.success('Job updated successfully!');
        navigate('/employer/dashboard');
      } else {
        response = await axios.post(`${API_URL}/jobs`, payload, {
          headers: {  },
          withCredentials: true,
        });
        
        if (showSkipWarning) {
          toast.info('Job posted as a free listing. You can feature it later from your dashboard.');
        } else {
          toast.success('Job posted successfully!');
        }
        
        // Redirect to subscription page to unlock candidate access
        navigate('/employer/subscription?from=post-job');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  const addListItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], ''],
    }));
  };

  const removeListItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const updateListItem = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item),
    }));
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-primary mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        {/* Welcome Banner for New Employers */}
        {isWelcome && !isEditing && (
          <div className="bg-gradient-to-r from-primary to-primary-hover text-white rounded-xl p-6 mb-6">
            <h2 className="font-serif text-xl font-bold mb-2">Welcome! Post Your First Job Free</h2>
            <p className="text-white/90 text-sm">
              Create your job listing below. It is completely free to post jobs. 
              After posting, you will have the option to subscribe to view and contact applicants.
            </p>
          </div>
        )}

        <div className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100">
          <h1 className="font-serif text-2xl font-bold text-gray-900 mb-6">
            {isEditing ? 'Edit Job' : 'Post a New Job'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="sm:col-span-2">
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Clinical Social Worker"
                  className="mt-1"
                  required
                  data-testid="job-title-input"
                />
              </div>

              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="mt-1"
                  required
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <Label htmlFor="location">Location *</Label>
                <LocationAutocomplete
                  id="location"
                  value={formData.location}
                  onChange={(value) => setFormData({ ...formData, location: value })}
                  placeholder="Start typing city name..."
                  className="mt-1"
                  required
                />
              </div>

              <div>
                <Label htmlFor="job_type">Job Type *</Label>
                <Select
                  value={formData.job_type}
                  onValueChange={(value) => setFormData({ ...formData, job_type: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="job-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {jobTypeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="license_required">License Required *</Label>
                <Select
                  value={formData.license_required}
                  onValueChange={(value) => setFormData({ ...formData, license_required: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="license-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {licenseOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Compensation (required) */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <Label className="text-primary font-semibold">Compensation <span className="text-red-500">*</span></Label>
              <p className="text-xs text-gray-500 mb-3">Required on INASW job posts. Candidates see this on the job card.</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label htmlFor="pay_type" className="text-sm">Pay Type</Label>
                  <select
                    id="pay_type"
                    value={formData.pay_type}
                    onChange={(e) => setFormData({ ...formData, pay_type: e.target.value })}
                    className="mt-1 w-full border rounded-md px-3 h-10 text-sm bg-white"
                    data-testid="pay-type-select"
                  >
                    <option value="salary">Salary</option>
                    <option value="hourly">Hourly</option>
                    <option value="contract">Contract</option>
                    <option value="stipend">Stipend</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="pay_frequency" className="text-sm">Pay Frequency</Label>
                  <select
                    id="pay_frequency"
                    value={formData.pay_frequency}
                    onChange={(e) => setFormData({ ...formData, pay_frequency: e.target.value })}
                    className="mt-1 w-full border rounded-md px-3 h-10 text-sm bg-white"
                    data-testid="pay-frequency-select"
                  >
                    <option value="year">per year</option>
                    <option value="month">per month</option>
                    <option value="week">per week</option>
                    <option value="hour">per hour</option>
                    <option value="visit">per visit</option>
                  </select>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="salary_min" className="text-sm">Minimum Pay <span className="text-red-500">*</span></Label>
                  <Input
                    id="salary_min"
                    type="number"
                    value={formData.salary_min}
                    onChange={(e) => setFormData({ ...formData, salary_min: e.target.value })}
                    placeholder="e.g., 50000"
                    className="mt-1"
                    required
                    data-testid="salary-min-input"
                  />
                </div>
                <div>
                  <Label htmlFor="salary_max" className="text-sm">Maximum Pay <span className="text-red-500">*</span></Label>
                  <Input
                    id="salary_max"
                    type="number"
                    value={formData.salary_max}
                    onChange={(e) => setFormData({ ...formData, salary_max: e.target.value })}
                    placeholder="e.g., 70000"
                    className="mt-1"
                    required
                    data-testid="salary-max-input"
                  />
                </div>
              </div>
            </div>

            {/* Listing duration */}
            {!isEditing && (
              <div>
                <Label>How long should this job stay live?</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2" data-testid="job-duration-group">
                  {[
                    { v: 30, label: '30 days' },
                    { v: 60, label: '60 days' },
                    { v: 90, label: '90 days' },
                    { v: null, label: 'Until I close it' },
                  ].map((opt) => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      onClick={() => setFormData({ ...formData, duration_days: opt.v })}
                      data-testid={`job-duration-${opt.v === null ? 'open' : opt.v}`}
                      className={`p-3 rounded-xl border-2 text-sm font-semibold transition-all ${
                        formData.duration_days === opt.v
                          ? 'border-primary bg-primary/5 text-primary shadow-sm'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Listings auto-expire after the selected window so your board stays fresh. You can renew or close anytime from your dashboard.
                </p>
              </div>
            )}


            {/* Remote */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_remote"
                checked={formData.is_remote}
                onCheckedChange={(checked) => setFormData({ ...formData, is_remote: checked })}
                data-testid="remote-checkbox"
              />
              <Label htmlFor="is_remote" className="cursor-pointer">
                This is a remote position
              </Label>
            </div>

            {/* Work setting, ZIP, specialty */}
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <Label htmlFor="remote_type">Work Setting</Label>
                <Select
                  value={formData.remote_type}
                  onValueChange={(v) => setFormData({ ...formData, remote_type: v, is_remote: v === 'remote' })}
                >
                  <SelectTrigger className="mt-1" data-testid="remote-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In-person</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="zip_code">ZIP Code</Label>
                <Input
                  id="zip_code"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="e.g., 78701"
                  className="mt-1"
                  data-testid="zip-code-input"
                />
              </div>
              <div>
                <Label htmlFor="specialty">Specialty / Practice Area</Label>
                <Input
                  id="specialty"
                  value={formData.specialty}
                  onChange={(e) => setFormData({ ...formData, specialty: e.target.value })}
                  placeholder="e.g., Medical, School, Behavioral Health"
                  className="mt-1"
                  data-testid="specialty-input"
                />
              </div>
            </div>

            {/* Employer-selected workplace attributes */}
            <div className="bg-primary/5 border border-primary/15 rounded-lg p-4">
              <Label className="text-sm font-semibold text-primary">Workplace attributes (optional, employer-selected)</Label>
              <p className="text-xs text-gray-500 mb-3">Select only what genuinely applies to your workplace. These power candidate job alerts.</p>
              <div className="flex flex-wrap gap-5">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={formData.lgbtq_affirming} onCheckedChange={(c) => setFormData({ ...formData, lgbtq_affirming: !!c })} data-testid="lgbtq-checkbox" /> LGBTQ+ affirming workplace
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={formData.veteran_friendly} onCheckedChange={(c) => setFormData({ ...formData, veteran_friendly: !!c })} data-testid="veteran-checkbox" /> Veteran-friendly workplace
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <Checkbox checked={formData.accessible_inclusive} onCheckedChange={(c) => setFormData({ ...formData, accessible_inclusive: !!c })} data-testid="accessible-checkbox" /> Accessible / disability-inclusive workplace
                </label>
              </div>
            </div>

            {/* Technology & Administrative Support (employer-reported) */}
            <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4" data-testid="tech-admin-section">
              <Label className="text-sm font-semibold text-primary">Technology &amp; Administrative Support (optional)</Label>
              <p className="text-xs text-gray-500 mb-3">Select what your organization provides. Shown as badges on the job and used in candidate filters.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {TECH_ADMIN_OPTIONS.map((opt) => {
                  const checked = formData.tech_admin_support.includes(opt);
                  const toggle = (c) => setFormData((prev) => {
                    let next = c ? [...prev.tech_admin_support, opt] : prev.tech_admin_support.filter((x) => x !== opt);
                    // "None provided" is mutually exclusive with the rest
                    if (c && opt === 'None provided') next = ['None provided'];
                    else if (c) next = next.filter((x) => x !== 'None provided');
                    return { ...prev, tech_admin_support: next };
                  });
                  return (
                    <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <Checkbox checked={checked} onCheckedChange={toggle} data-testid={`tech-admin-${opt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`} /> {opt}
                    </label>
                  );
                })}
              </div>
              <div className="mt-3">
                <Label className="text-xs text-gray-600">Other (short description)</Label>
                <Input
                  value={formData.tech_admin_support_other}
                  onChange={(e) => setFormData({ ...formData, tech_admin_support_other: e.target.value })}
                  placeholder="e.g. Dedicated peer-consultation group"
                  maxLength={120}
                  data-testid="tech-admin-other"
                />
              </div>
              <p className="text-xs text-gray-400 mt-2 italic">{TECH_ADMIN_DISCLAIMER}</p>
            </div>

            {/* Application Method */}
            <div>
              <Label htmlFor="application_method">Application Method</Label>
              <Select
                value={formData.application_method}
                onValueChange={(v) => setFormData({ ...formData, application_method: v })}
              >
                <SelectTrigger className="mt-1" data-testid="application-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inasw">Apply through I NEED A SOCIAL WORKER</SelectItem>
                  <SelectItem value="partner_ats" disabled={!atsConnected}>
                    Partner ATS — Direct Submission{!atsConnected ? ' (connection required)' : ''}
                  </SelectItem>
                  <SelectItem value="employer_external">Employer External Application Page</SelectItem>
                  <SelectItem value="secure_email">Secure Email Delivery</SelectItem>
                </SelectContent>
              </Select>
              {!atsConnected && (
                <p className="text-xs text-gray-400 mt-1">An approved and active ATS connection is required for Partner ATS — Direct Submission.</p>
              )}
              {formData.application_method === 'employer_external' && (
                <Input className="mt-2" placeholder="https://employer.com/apply" value={formData.external_application_url || ''} onChange={(e) => setFormData({ ...formData, external_application_url: e.target.value })} data-testid="external-app-url" />
              )}
              {formData.application_method === 'secure_email' && (
                <Input className="mt-2" placeholder="applications@employer.com" value={formData.secure_email || ''} onChange={(e) => setFormData({ ...formData, secure_email: e.target.value })} data-testid="secure-email-input" />
              )}
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label htmlFor="description">Job Description *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateAIDescription}
                  disabled={generatingDescription}
                  className="text-secondary border-secondary hover:bg-secondary/10"
                  data-testid="ai-generate-btn"
                >
                  {generatingDescription ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4 mr-1" />
                      AI Write
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe the role, responsibilities, and ideal candidate... or click 'AI Write' to generate!"
                rows={6}
                className="mt-1"
                required
                data-testid="description-input"
              />
            </div>

            {/* Requirements */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Requirements</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => addListItem('requirements')}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.requirements.map((req, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={req}
                      onChange={(e) => updateListItem('requirements', index, e.target.value)}
                      placeholder="e.g., 2+ years of experience"
                      data-testid={`requirement-${index}`}
                    />
                    {formData.requirements.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeListItem('requirements', index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Benefits</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => addListItem('benefits')}
                >
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              <div className="space-y-2">
                {formData.benefits.map((benefit, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={benefit}
                      onChange={(e) => updateListItem('benefits', index, e.target.value)}
                      placeholder="e.g., Health insurance"
                      data-testid={`benefit-${index}`}
                    />
                    {formData.benefits.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeListItem('benefits', index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Featured Job Option - Only for new jobs */}
            {!isEditing && (
              <div className="bg-gradient-to-r from-secondary/5 to-secondary/10 border-2 border-secondary/20 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-semibold text-gray-900">Feature This Job</h4>
                      <Badge className="bg-secondary text-white text-xs">$49</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      Get 3x more applicants with premium placement at the top of search results and a featured badge.
                    </p>
                    <ul className="space-y-1 mb-4">
                      {[
                        'Featured badge on listing',
                        'Top of search results for 30 days',
                        'Homepage spotlight',
                      ].map((feature, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle2 className="w-4 h-4 text-secondary" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="wantsFeatured"
                        checked={wantsFeatured}
                        onCheckedChange={setWantsFeatured}
                        data-testid="feature-job-checkbox"
                      />
                      <Label htmlFor="wantsFeatured" className="cursor-pointer font-medium text-secondary">
                        Yes, feature my job for $49
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="bg-primary hover:bg-primary-hover text-white"
                data-testid="submit-job-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isEditing ? 'Updating...' : 'Posting...'}
                  </>
                ) : (
                  isEditing ? 'Update Job' : 'Post Job'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Feature Job Confirmation Dialog */}
      <Dialog open={showFeatureConfirmDialog} onOpenChange={setShowFeatureConfirmDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-secondary" />
              Feature Your Job for $49
            </DialogTitle>
            <DialogDescription className="text-left pt-2">
              You&apos;ve selected to feature this job posting. Featured jobs get:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <ul className="space-y-2">
              {[
                'Premium placement at top of search results',
                'Featured badge highlighting your listing',
                'Homepage spotlight for 30 days',
                '3x more visibility and applicants',
              ].map((feature, i) => (
                <li key={`feature-${i}`} className="flex items-center gap-2 text-sm text-gray-700">
                  <CheckCircle2 className="w-4 h-4 text-secondary flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  If you skip payment, your job will be posted as a <strong>free listing</strong> without featured benefits.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleFeatureSkip}
              disabled={loading}
              className="w-full sm:w-auto"
              data-testid="skip-feature-btn"
            >
              Post as Free Listing
            </Button>
            <Button
              onClick={handleFeatureConfirm}
              disabled={loading}
              className="w-full sm:w-auto bg-secondary hover:bg-secondary/90 text-white"
              data-testid="proceed-payment-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay $49 & Feature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PostJob;
